'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Participant, Track } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import { DebouncedConnectionQualityIndicator } from '@/lib/DebouncedConnectionQualityIndicator';
import {
  CONNECTION_QUALITY_TRANSIENT_HINT,
} from '@/lib/connectionQualityDisplay';
import { useDebouncedConnectionQuality } from '@/lib/useDebouncedConnectionQuality';
import {
  fetchTrackStats,
  type RtcMediaStatsData,
  type RtcStatsPrevEntry,
} from '@/lib/rtcTrackStats';

type StatsData = RtcMediaStatsData;
type PrevEntry = RtcStatsPrevEntry;

const QUALITY_LABEL: Record<string, string> = {
  excellent: 'Excelente',
  good: 'Boa',
  poor: 'Ruim',
  lost: 'Perdida',
  unknown: 'Desconhecida',
};

const QUALITY_COLOR: Record<string, string> = {
  excellent: '#06db4d',
  good: '#ffd100',
  poor: '#f91f31',
  lost: '#f91f31',
  unknown: '#aaa',
};

interface StatsPanelProps {
  quality: string;
  stats: StatsData;
  isLocal: boolean;
  fixedTopRight?: boolean;
  rect?: DOMRect | null;
  transientHint?: boolean;
}

function StatsPanel({
  quality,
  stats,
  isLocal,
  fixedTopRight,
  rect,
  transientHint,
}: StatsPanelProps) {
  const qualityLabel = QUALITY_LABEL[quality] ?? quality;
  const qualityColor = QUALITY_COLOR[quality] ?? QUALITY_COLOR.unknown;

  const totalKbps = (stats.videoKbps ?? 0) + (stats.audioKbps ?? 0);
  const arrow = isLocal ? '↑' : '↓';
  const bitrateText =
    stats.videoKbps !== undefined || stats.audioKbps !== undefined
      ? `${arrow}${totalKbps} Kbps`
      : '—';

  const maxPacketLoss = Math.max(stats.videoPacketLoss ?? 0, stats.audioPacketLoss ?? 0);
  const packetLossText =
    stats.videoPacketLoss !== undefined || stats.audioPacketLoss !== undefined
      ? `${arrow}${maxPacketLoss}%`
      : '—';

  const codecs = [stats.audioCodec, stats.videoCodec].filter(Boolean).join(', ');

  const panelWidth = 228;
  const gap = 8;
  const positionStyles = fixedTopRight
    ? { top: 44, left: 12 }
    : rect
      ? (() => {
          // Ícone no topo: painel aparece abaixo do ícone
          const top = rect.bottom + gap;
          let left = rect.left;
          if (left + panelWidth > window.innerWidth - 16) {
            left = window.innerWidth - panelWidth - 16;
          }
          if (left < 16) left = 16;
          return { top, left };
        })()
      : { top: 44, left: 12 };

  return (
    <div
      style={{
        position: 'fixed',
        ...positionStyles,
        width: `${panelWidth}px`,
        backgroundColor: 'rgba(20, 20, 20, 0.92)',
        borderRadius: '8px',
        padding: '10px 14px',
        zIndex: 99999,
        boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
        backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,255,255,0.08)',
        pointerEvents: 'none',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        lineHeight: '1.4',
      }}
    >
      <StatRow label="Ligação:" value={<span style={{ color: qualityColor, fontWeight: 600 }}>{qualityLabel}</span>} />
      {transientHint && (
        <div
          style={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '11px',
            lineHeight: 1.35,
            padding: '4px 0 2px',
          }}
        >
          {CONNECTION_QUALITY_TRANSIENT_HINT}
        </div>
      )}
      <StatRow label="Taxa de bits:" value={bitrateText} />
      <StatRow label="Perda de pacote:" value={packetLossText} />
      {stats.width != null && stats.height != null && (
        <StatRow label="Resolução:" value={`${stats.width}x${stats.height}`} />
      )}
      {stats.fps != null && (
        <StatRow label="Taxa de frames:" value={String(stats.fps)} />
      )}
      {codecs && (
        <StatRow label="Codecs (A/V):" value={codecs} />
      )}
      <div
        style={{
          textAlign: 'center',
          paddingTop: '7px',
          marginTop: '6px',
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <span style={{ color: '#1f8cf9', fontSize: '12px' }}>Mostrar mais</span>
      </div>
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        padding: '2px 0',
      }}
    >
      <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

interface ConnectionQualityWithStatsProps {
  participant: Participant;
  videoSource?: Track.Source;
}

export function ConnectionQualityWithStats({
  participant,
  videoSource = Track.Source.Camera,
}: ConnectionQualityWithStatsProps) {
  const { quality, showTransientHint } = useDebouncedConnectionQuality(participant);
  const [hovered, setHovered] = useState(false);
  const [portalRect, setPortalRect] = useState<DOMRect | null>(null);
  const [stats, setStats] = useState<StatsData>({});
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevVideoRef = useRef<PrevEntry | null>(null);
  const prevAudioRef = useRef<PrevEntry | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const poll = useCallback(async () => {
    const result = await fetchTrackStats(participant, videoSource, prevVideoRef, prevAudioRef);
    setStats(result);
  }, [participant, videoSource]);

  useEffect(() => {
    if (!hovered) {
      prevVideoRef.current = null;
      prevAudioRef.current = null;
      return;
    }
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [hovered, poll]);

  const handleMouseEnter = () => {
    if (containerRef.current) {
      setPortalRect(containerRef.current.getBoundingClientRect());
    }
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  return (
    <>
      <div
        ref={containerRef}
        className="connection-quality-stats-wrapper"
        style={{
          display: 'inline-block',
          cursor: 'help',
          opacity: 1,
          ['--lk-connection-excellent-color' as string]: '#06db4d',
          ['--lk-connection-good-color' as string]: '#ffd100',
          ['--lk-connection-poor-color' as string]: '#f91f31',
          ['--lk-connection-lost-color' as string]: '#f91f31',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <DebouncedConnectionQualityIndicator participant={participant} />
      </div>
      {mounted &&
        hovered &&
        portalRect &&
        createPortal(
          <StatsPanel
            quality={quality}
            stats={stats}
            isLocal={participant.isLocal}
            fixedTopRight={false}
            rect={portalRect}
            transientHint={showTransientHint}
          />,
          document.body,
        )}
    </>
  );
}

export function ConnectionStatsOverlay() {
  const room = useRoomContext();
  const localParticipant = room.localParticipant;

  const [hovered, setHovered] = useState(false);
  const [stats, setStats] = useState<StatsData>({});
  const prevVideoRef = useRef<PrevEntry | null>(null);
  const prevAudioRef = useRef<PrevEntry | null>(null);

  const { quality, showTransientHint } = useDebouncedConnectionQuality(localParticipant);

  const poll = useCallback(async () => {
    const result = await fetchTrackStats(
      localParticipant,
      Track.Source.Camera,
      prevVideoRef,
      prevAudioRef,
    );
    setStats(result);
  }, [localParticipant]);

  useEffect(() => {
    if (!hovered) {
      prevVideoRef.current = null;
      prevAudioRef.current = null;
      return;
    }
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [hovered, poll]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 12,
        left: 12,
        zIndex: 99999,
        cursor: 'pointer',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <DebouncedConnectionQualityIndicator participant={localParticipant} />
      {hovered && (
        <StatsPanel
          quality={quality}
          stats={stats}
          isLocal={true}
          fixedTopRight={true}
          transientHint={showTransientHint}
        />
      )}
    </div>
  );
}
